/**
 * The two reports an Austrian small business actually needs: the UVA draft for FinanzOnline and
 * the Einnahmen-Ausgaben-Rechnung for the accountant.
 *
 * Both are drafts computed from booked documents only, and both say so. Filing happens in
 * FinanzOnline; this page exists so the figures are right before they are typed there.
 */
import { Download, FileCode, TriangleAlert } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MONTH_KEYS } from "@/lib/utils/period-label";
import { E1aDraft } from "@/components/reports/e1a-draft";
import { FilingDialog } from "@/components/reports/filing-dialog";
import { Stat } from "@/components/shared/stat";
import { useAllDocuments, useCategories } from "@/hooks/use-documents";
import {
  type Period,
  openFilingPeriod,
  periodsForYear,
  uvaDueDate,
  yearPeriod,
} from "@/lib/at/periods";
import {
  computeIncomeStatement,
  computeUva,
  depreciationLines,
  receiptlessLines,
  travelLines,
  UVA_CODES,
} from "@/lib/at/uva";
import { u30Xml, zmLines, zmXml } from "@/lib/at/finanzonline";
import { afaInput } from "@/lib/api/assets";
import { DEPRECIATION, TRAVEL } from "@/lib/ledger/money";
import { tripInput } from "@/lib/api/trips";
import { useTrips } from "@/hooks/use-trips";
import { useAssets } from "@/hooks/use-assets";
import { useAccounts, useTransactions } from "@/hooks/use-banking";
import { buildCsv, downloadCsv, uvaRows } from "@/lib/export/tables";
import { formatDate, formatMoney } from "@/lib/utils/format";
import { useWorkspace } from "@/lib/session/workspace-context";

export function ReportsPage() {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const basis = workspace?.accountingBasis ?? "cash";
  const vatPeriod = workspace?.vatPeriod ?? "quarterly";

  const today = new Date().toISOString().slice(0, 10);
  const defaultPeriod = useMemo(
    () => openFilingPeriod(today, vatPeriod === "none" ? "quarterly" : vatPeriod),
    [today, vatPeriod],
  );
  const [year, setYear] = useState(defaultPeriod.year);
  const [periodKey, setPeriodKey] = useState(defaultPeriod.key);
  const [filing, setFiling] = useState<"u30" | "zm" | null>(null);

  // The whole year too: the income statement a self-filer copies into the E1a is annual.
  const periods = useMemo(
    () => [
      ...periodsForYear(year, vatPeriod === "none" ? "quarterly" : vatPeriod),
      yearPeriod(year),
    ],
    [year, vatPeriod],
  );
  const period: Period = periods.find((entry) => entry.key === periodKey) ?? periods[0]!;

  const { data: documents, isPending } = useAllDocuments(workspace?.id);
  const { data: categories } = useCategories(workspace?.id);
  const { data: transactions } = useTransactions(workspace?.id);
  const { data: accounts } = useAccounts(workspace?.id);
  const { data: assets } = useAssets(workspace?.id);
  const { data: trips } = useTrips(workspace?.id);

  const uva = useMemo(() => computeUva(documents ?? [], period, basis), [documents, period, basis]);
  const zm = useMemo(() => zmLines(documents ?? [], period), [documents, period]);
  // Receipt-less bookings and trips; the AfA is added per period below and split by method in
  // the E1a draft.
  const lines = useMemo(
    () => [
      ...receiptlessLines(
        transactions ?? [],
        categories ?? [],
        Object.fromEntries((accounts ?? []).map((account) => [account.id, account.openingDate])),
      ),
      ...travelLines((trips ?? []).map(tripInput), {
        name: t("reports.travel"),
        accountNumber: TRAVEL,
      }),
    ],
    [transactions, categories, accounts, trips, t],
  );
  const statementFor = useCallback(
    (range: Period, withDepreciation: boolean) =>
      computeIncomeStatement(documents ?? [], categories ?? [], range, basis, {
        useGross: workspace?.vatScheme === "small_business",
        uncategorizedLabel: t("reports.uncategorized"),
        lines: withDepreciation
          ? [
              ...lines,
              ...depreciationLines((assets ?? []).map(afaInput), {
                name: t("reports.depreciation"),
                accountNumber: DEPRECIATION,
              }),
            ]
          : lines,
        // Purchases in the asset register: their AfA is the expense, not the invoice.
        excludeDocuments: new Set(
          (assets ?? []).map((asset) => asset.document).filter(Boolean) as string[],
        ),
      }),
    [documents, categories, lines, assets, basis, workspace?.vatScheme, t],
  );
  const statement = useMemo(() => statementFor(period, true), [statementFor, period]);
  const e1aStatement = useMemo(() => statementFor(yearPeriod(year), false), [statementFor, year]);
  const previousTurnover = useMemo(
    () => statementFor(yearPeriod(year - 1), false).revenueTotal,
    [statementFor, year],
  );

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [current + 1, current, current - 1, current - 2];
  }, []);

  function exportUva() {
    downloadCsv(
      `uva-${period.key}.csv`,
      buildCsv([t("reports.code"), t("reports.amount")], uvaRows(uva)),
    );
  }

  function exportStatement() {
    const header = [
      t("reports.account"),
      t("reports.category"),
      t("reports.amount"),
      t("reports.deductible"),
      t("reports.documents"),
    ];
    const rows = [
      ...statement.revenue.map((row) => [
        row.accountNumber ?? "",
        row.name,
        row.amount.toFixed(2).replace(".", ","),
        row.deductible.toFixed(2).replace(".", ","),
        row.count,
      ]),
      ...statement.expenses.map((row) => [
        row.accountNumber ?? "",
        row.name,
        (-row.amount).toFixed(2).replace(".", ","),
        (-row.deductible).toFixed(2).replace(".", ","),
        row.count,
      ]),
    ];
    downloadCsv(`ea-${period.key}.csv`, buildCsv(header, rows));
  }

  if (isPending) return <Skeleton className="h-96 w-full" aria-busy="true" />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("reports.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t(`reports.basis.${basis}`)} · {t("reports.subtitle")}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground" htmlFor="year">
            {t("reports.year")}
          </Label>
          <Select
            value={String(year)}
            onValueChange={(value) => {
              const next = Number(value);
              setYear(next);
              setPeriodKey(
                periodsForYear(next, vatPeriod === "none" ? "quarterly" : vatPeriod)[0]!.key,
              );
            }}
          >
            <SelectTrigger id="year" className="w-32 num" data-testid="report-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((entry) => (
                <SelectItem key={entry} value={String(entry)}>
                  {entry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground" htmlFor="period">
            {t("reports.period")}
          </Label>
          <Select value={period.key} onValueChange={setPeriodKey}>
            <SelectTrigger id="period" className="w-48" data-testid="report-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periods.map((entry) => (
                <SelectItem key={entry.key} value={entry.key}>
                  {entry.kind === "monthly"
                    ? t(MONTH_KEYS[entry.index - 1]!)
                    : entry.kind === "quarterly"
                      ? t("reports.quarter", { index: entry.index })
                      : t("reports.wholeYear")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {uva.pendingCount > 0 && (
        <Alert data-testid="reports-pending">
          <TriangleAlert className="size-4" />
          <AlertTitle>{t("reports.pendingTitle", { count: uva.pendingCount })}</AlertTitle>
          <AlertDescription>{t("reports.pendingText")}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="uva">
        <TabsList>
          <TabsTrigger value="uva" data-testid="tab-uva">
            {t("reports.uvaTab")}
          </TabsTrigger>
          <TabsTrigger value="ea" data-testid="tab-ea">
            {t("reports.eaTab")}
          </TabsTrigger>
          <TabsTrigger value="e1a" data-testid="tab-e1a">
            {t("reports.e1aTab", { year })}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="uva" className="space-y-6 pt-4">
          {workspace?.vatScheme === "small_business" ? (
            <Alert data-testid="small-business-note">
              <AlertTitle>{t("reports.smallBusinessTitle")}</AlertTitle>
              <AlertDescription>{t("reports.smallBusinessText")}</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="grid gap-6 rounded-lg border bg-card p-6 sm:grid-cols-3">
                <Stat
                  emphasis
                  tone={uva.payable > 0 ? "brand" : "success"}
                  label={uva.payable >= 0 ? t("reports.payable") : t("reports.credit")}
                  value={formatMoney(Math.abs(uva.payable))}
                  testId="uva-payable"
                  hint={t("reports.code095")}
                />
                <Stat label={t("reports.vatOwed")} value={formatMoney(uva.vatOwed)} />
                <Stat
                  label={t("reports.inputTax")}
                  value={formatMoney(uva.inputTax)}
                  hint={t("reports.dueOn", { date: formatDate(uvaDueDate(period)) })}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={exportUva} data-testid="export-uva">
                  <Download /> {t("common.export")}
                </Button>
                {period.kind !== "none" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiling("u30")}
                    data-testid="file-u30"
                  >
                    <FileCode /> {t("filing.button")}
                  </Button>
                )}
                {period.kind !== "none" && zm.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiling("zm")}
                    data-testid="file-zm"
                  >
                    <FileCode /> {t("filing.zmButton")}
                  </Button>
                )}
              </div>
              <FilingDialog
                key={period.key}
                form="u30"
                open={filing === "u30"}
                onOpenChange={(open) => setFiling(open ? "u30" : null)}
                fileName={`UVA_${period.key}.xml`}
                needsParagraph={uva.amounts["020"] !== 0}
                build={(exemptParagraph) =>
                  u30Xml({
                    uva,
                    period,
                    taxNumber: workspace?.taxNumber ?? "",
                    exemptParagraph,
                    created: new Date(),
                    today,
                  })
                }
              />
              <FilingDialog
                key={`zm-${period.key}`}
                form="zm"
                open={filing === "zm"}
                onOpenChange={(open) => setFiling(open ? "zm" : null)}
                fileName={`ZM_${period.key}.xml`}
                build={() =>
                  zmXml({
                    lines: zm,
                    period,
                    taxNumber: workspace?.taxNumber ?? "",
                    created: new Date(),
                    today,
                  })
                }
              />

              <div className="overflow-hidden rounded-lg border">
                <Table data-testid="uva-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">{t("reports.code")}</TableHead>
                      <TableHead>{t("reports.description")}</TableHead>
                      <TableHead className="text-right">{t("reports.amount")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {UVA_CODES.filter((code) => uva.amounts[code] !== 0 || code === "095").map(
                      (code) => (
                        <TableRow key={code} data-testid={`uva-row-${code}`}>
                          <TableCell className="ledger-id">{code}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {t(`uvaCodes.${code}`)}
                          </TableCell>
                          <TableCell className="text-right font-medium num">
                            {formatMoney(uva.amounts[code])}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </div>

              <p className="text-xs text-muted-foreground">{t("reports.disclaimer")}</p>
            </>
          )}
        </TabsContent>

        <TabsContent value="ea" className="space-y-6 pt-4">
          <div className="grid gap-6 rounded-lg border bg-card p-6 sm:grid-cols-3">
            <Stat label={t("reports.revenue")} value={formatMoney(statement.revenueTotal)} />
            <Stat label={t("reports.expenses")} value={formatMoney(statement.expenseTotal)} />
            <Stat
              emphasis
              tone={statement.profit >= 0 ? "success" : "warning"}
              label={statement.profit >= 0 ? t("reports.profit") : t("reports.loss")}
              value={formatMoney(Math.abs(statement.profit))}
              testId="ea-profit"
            />
          </div>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={exportStatement} data-testid="export-ea">
              <Download /> {t("common.export")}
            </Button>
          </div>

          {(["revenue", "expenses"] as const).map((section) => (
            <div key={section} className="space-y-2">
              <h2 className="font-medium">{t(`reports.${section}`)}</h2>
              {statement[section].length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("reports.nothingInPeriod")}</p>
              ) : (
                <div className="overflow-hidden rounded-lg border">
                  <Table data-testid={`ea-${section}`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">{t("reports.account")}</TableHead>
                        <TableHead>{t("reports.category")}</TableHead>
                        <TableHead className="text-right">{t("reports.amount")}</TableHead>
                        {section === "expenses" && (
                          <TableHead className="text-right">{t("reports.deductible")}</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {statement[section].map((row) => (
                        <TableRow key={row.categoryId} data-testid="ea-row">
                          <TableCell className="ledger-id">{row.accountNumber ?? "—"}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell className="text-right num">
                            {formatMoney(row.amount)}
                          </TableCell>
                          {section === "expenses" && (
                            <TableCell className="text-right font-medium num">
                              {formatMoney(row.deductible)}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ))}

          <p className="text-xs text-muted-foreground">{t("reports.eaDisclaimer")}</p>
        </TabsContent>

        <TabsContent value="e1a" className="pt-4">
          <E1aDraft
            year={year}
            statement={e1aStatement}
            categories={categories ?? []}
            assets={(assets ?? []).map(afaInput)}
            previousTurnover={previousTurnover}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
